/**
 * Authentication Error Class
 *
 * Why: Specialized handling for authentication failures
 * What: Error class for 401 unauthorized response
 *
 * Benefits:
 *      - Trigger auth flows (logout,token refreh)
 *      - Different handling for expired vs invalid tokens
 *      - Clear user communication about session state
 *
 * Use Cases:
 *  -Session expired
 *  =Invalid credentials
 *  -Token expired/invalid
 *  -Account locked/disabled
 */

import { AppError, type AppErrorOptions } from "./base/AppError";
import { ErrorCategory, ErrorSeverity } from "../types";
import { ERROR_CODES, ERROR_MESSAGES } from "../constants";

/**
 * Authrntication action required after error
 */
export const AuthAction = {
  /** User should be logged out */
  LOGOUT: "logout",
  /** No action needed e.g invalid credentials on login */
  NONE: "none",
} as const;

export type AuthAction = (typeof AuthAction)[keyof typeof AuthAction];

/**
 * Options specific to authentication errors
 */
export interface AuthenticationErrorOptions extends Omit<
  AppErrorOptions,
  "statusCode"
> {
  // Recommended action to take
  action?: AuthAction;
}

/**
 * Authentication Error
 *
 * Represents authentication failures.
 * Provides guidence on what action to take
 */
export class AuthenticationError extends AppError {
  /**
   * Recomended action to take
   */
  public readonly action: AuthAction;

  constructor(
    message: string = ERROR_MESSAGES.UNAUTHORIZED,
    options: AuthenticationErrorOptions = {},
  ) {
    super(message, {
      code: options.code ?? ERROR_CODES.AUTH_UNAUTHORIZED,
      category: ErrorCategory.AUTHENTICATION,
      severity: options.severity ?? ErrorSeverity.MEDIUM,
      isOperational: true,
      isRetryable: false,
      context: options.context,
      cause: options.cause,
    });
    this.action = options.action ?? AuthAction.LOGOUT;
  }
}
